import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgbService } from '../../../00_modules/master-data/services/agb.service';
import { DownloadLinkAreaComponent } from '../../../00_modules/shared/components/download-link-area/download-link-area.component';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, lastValueFrom, Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { UserFacade } from '../../../store/facade/user.facade';
import { Customer } from '../../../00_modules/organisation/customers/Customer';
import { OrderLeavingEditModeAction } from '../../../00_modules/core/models/hubActions/order-leaving-edit-mode.action';
import { NotificationFacade } from '../../../store/facade/notification.facade';
import { ChannelService } from '../../../00_modules/core/services/channel.service';
import { CommonRoutingSegments } from '../../../00_modules/core/services/common-routing-segments';
import { RoleAccessor } from '../../../00_modules/core/services/accessors/user-role/user-role.accessor';
import { RouterService } from '../../../00_modules/core/services/router.service';
import { UserService } from '../../../00_modules/core/services/user.service';
import { Router, RouterLink } from '@angular/router';
import { SubmitErrorComponent } from '../../../00_modules/submitError/components/inspection/submitError.component';
import { Role } from '../../../00_modules/core/user/Role';
import { RoleName } from '../../../00_modules/core/user/RoleName';
import { CustomerFacade } from '../../../store/facade/customer.facade';
import { DisplayMode } from '../../../00_modules/user-profile/enums/display-mode.enum';
import { DisplayModeService } from '../../../00_modules/shared/services/display-mode.service';
import { UserSettingsService } from '../../../00_modules/user-profile/services/user-settings.service';
import { IconComponent } from '../../../00_modules/shared/components/fa-icon/fa-icon.component';

import { JeDialogService } from '../../../00_modules/shared/components/dialog/dialog.service';
@Component({
    selector: 'je-application-header',
    imports: [CommonModule, TranslateModule, RouterLink, IconComponent, DownloadLinkAreaComponent],
    templateUrl: './application-header.component.html',
})
export class ApplicationHeaderComponent implements OnInit {
    public mobileMenuOpen = false;
    public logoUrl$: Observable<void | SafeUrl>;

    public username$: Observable<string>;
    public partnerName$: Observable<string>;
    public impersonate$: Observable<boolean>;
    public role: Role;
    public roles: Role[];
    public partnerId: number;
    public displayMode: DisplayMode = DisplayMode.lightMode;

    public showCustomerSelection: boolean = false;
    public showPartnerSelection: boolean = false;

    public customer$: Observable<Customer>;
    protected readonly DisplayMode = DisplayMode;

    constructor(
        private httpClient: HttpClient,
        private domSanitizer: DomSanitizer,
        private userFacade: UserFacade,
        private notificationFacade: NotificationFacade,
        private userService: UserService,
        private channelService: ChannelService,
        private router: Router,
        private roleAccessor: RoleAccessor,
        private dialog: JeDialogService,
        private routerService: RouterService,
        private customerFacade: CustomerFacade,
        private displayModeService: DisplayModeService,
        private userSettingsService: UserSettingsService,
        private agbService: AgbService,
    ) {
        this.username$ = this.userFacade.fullName$;
        this.partnerName$ = this.userFacade.userPartnerName$;
        this.impersonate$ = this.userFacade.impersonate$;
        this.customer$ = this.customerFacade.customerState$;
        this.userFacade.currentRole$.subscribe({
            next: role => {
                this.role = role;
            },
        });

        this.userFacade.userRoles$.subscribe({
            next: roles => {
                this.roles = roles;
            },
        });
    }

    async ngOnInit(): Promise<void> {
        this.userFacade.currentRoleId$.subscribe(roleId => {
            if (roleId === RoleName.OrderRecording) {
                this.showCustomerSelection = true;
                this.showPartnerSelection = false;
            } else {
                this.showCustomerSelection = false;
                this.showPartnerSelection = true;
            }
        });

        this.userFacade.userPartnerId$.subscribe({
            next: partnerId => {
                this.partnerId = partnerId;
                this.logoUrl$ = this.loadLogo(); // reload logo
            },
        });

        this.loadLogo();

        this.userFacade.userId$.subscribe(userId => {
            if (userId > 0) {
                this.userSettingsService.initialize(userId);
            }
        });

        this.displayModeService.displayMode$.subscribe(mode => {
            if (mode === DisplayMode.lightMode) {
                this.displayMode = DisplayMode.lightMode;
                document.documentElement.classList.remove('dark');
            } else {
                this.displayMode = DisplayMode.darkMode;
                document.documentElement.classList.add('dark');
            }
        });
    }

    public async onLogout(): Promise<void> {
        await this.unloadNotification();
        this.userService.logout();
    }

    public toggleDisplayMode() {
        if (this.displayMode === DisplayMode.lightMode) {
            this.displayModeService.setDisplayMode(DisplayMode.darkMode);
        } else {
            this.displayModeService.setDisplayMode(DisplayMode.lightMode);
        }
    }

    @HostListener('window:beforeunload')
    public async unloadNotification(): Promise<void> {
        const inEdit = await lastValueFrom(this.notificationFacade.orderInEditMode$.pipe(take(1)));
        if (inEdit && inEdit.orderId && inEdit.user) {
            const action = new OrderLeavingEditModeAction(inEdit.user, inEdit.orderId);
            await this.channelService.invokeAction(action);
        }
    }

    public onPartnerChange(): void {
        this.routerService.navigateToPartnerSelection();
    }

    public onCustomerChange(): void {
        this.customerFacade.resetCurrentCustomer();
        this.routerService.navigateToCustomerSelection();
    }

    public toggleMobileMenu(): void {
        this.mobileMenuOpen = !this.mobileMenuOpen;
    }

    public onProfileClicked(): void {
        if (this.roleAccessor.isCustomerRole()) {
            return;
        }
        this.router.navigateByUrl('/' + CommonRoutingSegments.UserProfile);
    }

    public onErrorSubmit(): void {
        this.dialog.open(SubmitErrorComponent);
    }

    public async onDownloadAgb(): Promise<void> {
        const partnerId = await this.getPartnerId();
        this.agbService.getAgb(partnerId);
    }

    private async getPartnerId(): Promise<number> {
        return new Promise<number>(resolve => {
            this.userFacade.user$.pipe(take(1)).subscribe(user => resolve(user.partnerId));
        });
    }

    public onRoleChange(): void {
        this.routerService.navigateToRoleSelection();
    }

    private loadLogo(): Observable<void | SafeUrl> {
        const imgUrl = `api/users/${this.partnerId}/business-partner-logo`;
        return this.loadImageFromUrl(imgUrl);
    }

    private loadImageFromUrl(imgUrl: string) {
        if (imgUrl) {
            return this.httpClient
                .get(imgUrl, {
                    responseType: 'blob',
                })
                .pipe(
                    map(imageBlob => {
                        if (imageBlob) {
                            return this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(imageBlob));
                        }
                        return null;
                    }),
                    catchError(async error => console.log(`Logo not exists for customer`)),
                );
        }
        return EMPTY;
    }

    protected readonly RoleName = RoleName;
}
